import { useTranslation } from "react-i18next";
import type { ToolInterface } from "../../../api/content/authoring/processContentDecoders";
import type { ToolStepDefinition } from "../../../api/content/authoring/stepDecoders";
import ErrorNotice from "../../../errors/ErrorNotice";
import { reportUiError, toUiError } from "../../../errors/uiError";
import LoadingIndicator from "../../../ui/feedback/LoadingIndicator";
import { FORM_COMPACT_CONTROL_CLASS, FORM_HELP_TEXT_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import { itemsOfKind, type AuthoringOptions } from "../draft/authoringOptions";
import FieldProblems from "../draft/FieldProblems";
import type { DefinitionCheck } from "../draft/useDefinitionCheck";
import Section from "../../../ui/forms/Section";
import { selectedItemId } from "./selectorChoices";
import { withParameter, withTool, withToolInputSource } from "./stepDefinitions";
import ParameterControl from "./ParameterControl";
import SourceEditor from "./SourceEditor";
import { readToolInterface, useToolInterface } from "./useToolInterface";
import OptionLabel from "../../../ui/forms/OptionLabel";
import EmptyState from "../../../ui/feedback/EmptyState";

interface ToolStepFieldsProps {
  step: ToolStepDefinition;
  options: AuthoringOptions;
  check: DefinitionCheck;
  onChange: (step: ToolStepDefinition) => void;
}

/** A tool step (AU-4): one reviewed tool or built-in, fed exactly the inputs it declares. */
export default function ToolStepFields({ step, options, check, onChange }: ToolStepFieldsProps) {
  const { t } = useTranslation();
  const catalogue = options.catalogue.status === "loaded" ? options.catalogue.value : null;
  const declared = useToolInterface(step.tool, catalogue);
  const tools = itemsOfKind(options, "tool");
  const builtIns = catalogue?.builtInTools ?? [];

  const chooseTool = (tool: string) => {
    // The inputs and parameters follow the tool; one that cannot be read is chosen as it is.
    readToolInterface(tool, catalogue, new AbortController().signal).then(
      (found) => {
        onChange(found === null ? { ...step, tool } : withTool(step, tool, found));
      },
      (caught: unknown) => {
        reportUiError(toUiError(caught));
        onChange({ ...step, tool });
      },
    );
  };

  return (
    <>
      <Section title={t("authoring.step.tool.title")} hint={t("authoring.step.tool.hint")}>
        <select className={FORM_SELECT_CLASS} aria-label={t("authoring.step.tool.choose")} value={step.tool} onChange={(event) => { chooseTool(event.target.value); }}>
          <option value="">{t("authoring.step.tool.choose")}</option>
          {tools.map((tool) => <option key={tool.reference} value={tool.reference}>{tool.name} · {t("authoring.step.tool.reviewed")}</option>)}
          {builtIns.map((tool) => <option key={tool.reference} value={tool.reference}>{tool.reference} · {t("authoring.step.tool.builtIn")}</option>)}
          {step.tool !== "" && !tools.some((tool) => tool.reference === step.tool) && !builtIns.some((tool) => tool.reference === step.tool) ? <option value={step.tool}>{step.tool}</option> : null}
        </select>
        <FieldProblems check={check} path="tool" />
        {declared.status === "loading" && step.tool !== "" ? <LoadingIndicator label={t("authoring.step.tool.loading")} /> : null}
        {declared.status === "failed" ? <ErrorNotice error={declared.error} compact={true} /> : null}
        {declared.status === "loaded" && declared.value !== null ? <p className={FORM_HELP_TEXT_CLASS}>{declared.value.description}</p> : null}
      </Section>
      {declared.status === "loaded" && declared.value !== null ? <DeclaredFields step={step} declared={declared.value} options={options} check={check} onChange={onChange} /> : null}
    </>
  );
}

function DeclaredFields({ step, declared, options, check, onChange }: ToolStepFieldsProps & { declared: ToolInterface }) {
  const { t } = useTranslation();
  const itemOutputs = Object.entries(declared.outputs).filter(([, output]) => output.kind === "item");
  return (
    <>
      <Section title={t("authoring.step.tool.inputs")} hint={t("authoring.step.tool.inputsHint")}>
        {Object.keys(declared.inputs).length === 0 ? <EmptyState size="small">{t("authoring.step.tool.noInputs")}</EmptyState> : null}
        <ul className="space-y-3">
          {Object.entries(declared.inputs).map(([name, input]) => (
            <li key={name} className="space-y-1">
              <p className="text-sm">
                <span className="font-mono font-semibold">{name}</span>{" "}
                <span className="app-text-muted text-xs">
                  {input.required ? t("authoring.step.required") : t("authoring.step.optional")} · {input.accepts.join(", ")}
                </span>
              </p>
              {input.description === null ? null : <p className={FORM_HELP_TEXT_CLASS}>{input.description}</p>}
              <SourceEditor
                definition={step}
                label={name}
                slotName={name}
                source={step.inputs[name] ?? null}
                slot={(() => {
                  const source = step.inputs[name];
                  return source?.source === "input" ? (step.input_slots[source.input] ?? null) : null;
                })()}
                options={options}
                accepts={input.accepts}
                optional={!input.required}
                onChange={(source, slot) => {
                  const next = withToolInputSource(step, name, source, input.accepts, input.required);
                  onChange(slot === undefined || source?.source !== "input" ? next : { ...next, input_slots: { ...next.input_slots, [source.input]: slot } });
                }}
              />
              <FieldProblems check={check} path={`inputs.${name}`} />
            </li>
          ))}
        </ul>
      </Section>
      {Object.keys(declared.parameters).length === 0 ? null : (
        <Section title={t("authoring.step.tool.parameters")} hint={t("authoring.step.tool.parametersHint")}>
          <ul className="space-y-2">
            {Object.entries(declared.parameters).map(([name, parameter]) => (
              <li key={name}>
                <ParameterControl name={name} parameter={parameter} value={step.parameters[name]} onChange={(value) => { onChange(withParameter(step, name, value)); }} />
                <FieldProblems check={check} path={`parameters.${name}`} />
              </li>
            ))}
          </ul>
        </Section>
      )}
      <Section title={t("authoring.step.tool.propose")} hint={t("authoring.step.tool.proposeHint")}>
        {itemOutputs.length === 0 ? (
          <EmptyState size="small">{t("authoring.step.tool.noItemOutput")}</EmptyState>
        ) : (
          <ProposeFields step={step} outputs={itemOutputs.map(([name]) => name)} options={options} onChange={onChange} />
        )}
      </Section>
    </>
  );
}

type ProposeOutput = NonNullable<ToolStepDefinition["propose"]>;
type ItemTarget = Exclude<ProposeOutput["as"], { readonly kind: "corpus" }>;

/** Where an item output is proposed: a new artifact or a new version of one. */
function ItemTargetFields({ propose, artifacts, onChange }: { propose: ProposeOutput & { as: ItemTarget }; artifacts: ReturnType<typeof itemsOfKind>; onChange: (propose: ProposeOutput) => void }) {
  const { t } = useTranslation();
  const target = propose.as;
  return (
    <>
      <select
        className={`${FORM_SELECT_CLASS} w-auto`}
        aria-label={t("authoring.step.tool.proposedAs")}
        value={target.item === "new" ? "new" : selectedItemId(target.item)}
        onChange={(event) => { onChange({ ...propose, as: { ...target, item: event.target.value === "new" ? "new" : { item: event.target.value } } }); }}
      >
        <option value="new">{t("authoring.step.tool.newArtifact")}</option>
        {artifacts.map((artifact) => <option key={artifact.itemId} value={artifact.itemId}>{artifact.name}</option>)}
      </select>
      <input className={`${FORM_COMPACT_CONTROL_CLASS} w-56`} aria-label={t("authoring.step.tool.proposedName")} value={target.name} onChange={(event) => { onChange({ ...propose, title: event.target.value, as: { ...target, name: event.target.value } }); }} />
    </>
  );
}

function ProposeFields({ step, outputs, options, onChange }: { step: ToolStepDefinition; outputs: readonly string[]; options: AuthoringOptions; onChange: (step: ToolStepDefinition) => void }) {
  const { t } = useTranslation();
  const propose = step.propose;
  const artifacts = itemsOfKind(options, "artifact");
  return (
    <div className="space-y-2 text-sm">
      <OptionLabel>
        <input
          type="checkbox"
          checked={propose !== null}
          onChange={(event) => {
            onChange({ ...step, propose: event.target.checked ? { output: outputs[0] ?? "", title: step.name, as: { kind: "artifact", item: "new", name: step.name } } : null });
          }}
        />
        {t("authoring.step.tool.proposeOutput")}
      </OptionLabel>
      {propose === null ? null : (
        <div className="flex flex-wrap items-center gap-2 border-l-2 pl-3 text-xs">
          <select className={`${FORM_SELECT_CLASS} w-auto`} aria-label={t("authoring.step.tool.proposedOutput")} value={propose.output} onChange={(event) => { onChange({ ...step, propose: { ...propose, output: event.target.value } }); }}>
            {outputs.map((output) => <option key={output} value={output}>{output}</option>)}
          </select>
          {propose.as.kind === "corpus" ? (
            <span>{t("authoring.step.tool.intoBoundCorpus", { mode: propose.as.mode })}</span>
          ) : (
            <ItemTargetFields propose={{ ...propose, as: propose.as }} artifacts={artifacts} onChange={(changed) => { onChange({ ...step, propose: changed }); }} />
          )}
        </div>
      )}
    </div>
  );
}
